import axios from "axios";
import { toast } from "sonner";

/**
 * Pick the API base URL at RUNTIME rather than baking it from
 * `REACT_APP_BACKEND_URL` at build time.
 *
 * Why this matters for deployment:
 *   - CRA/craco inlines `process.env.REACT_APP_BACKEND_URL` at compile time.
 *     When the build happens with the preview URL in `.env`, the bundle
 *     permanently points at the preview backend — even after deploy moves
 *     the frontend to a new domain.
 *   - That makes every API call cross-origin to the wrong host, triggers
 *     CORS preflight, and (critically) turns the auth cookies into
 *     third-party cookies. Chrome + Safari now block third-party cookies by
 *     default regardless of SameSite=None, so the cookie is set on /login
 *     but never replayed — producing the exact 401-after-login symptom.
 *
 * Both the Emergent preview ingress AND the production deploy ingress serve
 * the frontend and the backend on the SAME hostname (routing /api/* to the
 * backend). So `window.location.origin` is a correct runtime source of
 * truth on both. We only fall back to REACT_APP_BACKEND_URL for local dev
 * (localhost/127.0.0.1), where the dev server runs on port 3000 and can't
 * proxy /api on its own.
 */
function pickBackendUrl() {
  if (typeof window === "undefined") {
    return process.env.REACT_APP_BACKEND_URL || "";
  }
  const host = window.location.hostname;
  const isLocal = host === "localhost" || host === "127.0.0.1";
  if (isLocal && process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  return window.location.origin;
}

const BACKEND_URL = pickBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach bearer token if present (cookie fallback)
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("accp_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// ---------------------------------------------------------------------------
// Global 401 handler with refresh-token retry. AuthProvider registers its own
// setUser/navigate hooks via `registerAuthHandlers()` at mount — the
// interceptor first tries to silently refresh the access token, and only if
// that fails does it call the handler to clean up state and bounce the user
// to /login with a session-expired toast.
// ---------------------------------------------------------------------------
let _onUnauthenticated = null;

export function registerAuthHandlers(onUnauthenticated) {
  _onUnauthenticated = onUnauthenticated;
}

// Paths that should NEVER trigger the session-expired redirect — either
// because they're pre-login probes or because they're public-token-authed.
const SKIP_REDIRECT_PATHS = [
  "/auth/login",
  "/auth/logout",
  "/auth/refresh",       // circuit-breaker — never try to refresh a refresh
  "/auth/me",            // the mount-time probe — 401 here just means
                         //   "not yet logged in"; let AuthProvider handle it.
  "/audit-view",         // public inspector view (token IS the auth)
  "/audit-view/pdf",
  "/audit-digest/snooze",
  "/auth/device-alert",  // review-alert flow handles its own 401s (step-up / auth-required)
];

function _shouldSkipRedirect(url = "") {
  // url can be relative (`/auth/me`) or absolute. Compare by path suffix.
  try {
    const path = url.startsWith("http")
      ? new URL(url).pathname.replace(/^\/api/, "")
      : url;
    return SKIP_REDIRECT_PATHS.some((p) => path.startsWith(p));
  } catch {
    return false;
  }
}

// Auth endpoints that must NEVER trigger a silent refresh retry — doing so
// would cause infinite loops. `/auth/me` is NOT in this list because it's the
// exact probe that benefits most from silent refresh.
const NO_RETRY_AUTH_PATHS = [
  "/auth/login",
  "/auth/logout",
  "/auth/refresh",
];

function _isNoRetryAuth(url = "") {
  try {
    const path = url.startsWith("http")
      ? new URL(url).pathname.replace(/^\/api/, "")
      : url;
    return NO_RETRY_AUTH_PATHS.some((p) => path.startsWith(p));
  } catch {
    return false;
  }
}

// Dedupe in-flight refresh attempts: if 10 requests 401 in parallel, they all
// await the same refresh promise, rather than storming the server with 10
// `/auth/refresh` calls.
let _refreshPromise = null;
async function _silentRefresh() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = axios
    .post(`${API_BASE}/auth/refresh`, null, { withCredentials: true })
    .then(() => true)
    .catch(() => false)
    .finally(() => { _refreshPromise = null; });
  return _refreshPromise;
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err?.response?.status;
    const cfg = err?.config || {};
    const url = cfg.url || "";

    // Non-401s, login/logout/refresh endpoints, and requests we've already
    // retried fall straight through to the caller's `.catch`.
    if (status !== 401 || cfg._retry || _isNoRetryAuth(url)) {
      return Promise.reject(err);
    }

    // Try a silent refresh once; if it succeeds, replay the original request.
    cfg._retry = true;
    const refreshed = await _silentRefresh();
    if (refreshed) {
      return api.request(cfg);
    }

    // Refresh failed (no refresh cookie, revoked, expired, bad user) — do the
    // old redirect path.
    if (!_shouldSkipRedirect(url)) {
      // eslint-disable-next-line no-console
      console.warn("401 intercepted — redirecting to login", { url });
      if (_onUnauthenticated) {
        _onUnauthenticated();
      } else {
        const next = window.location.pathname + window.location.search;
        if (!window.location.pathname.startsWith("/login")) {
          window.location.replace(`/login?next=${encodeURIComponent(next)}`);
        }
      }
    }
    return Promise.reject(err);
  },
);

// CRA dev-mode `react-error-overlay` (v6.1.0) surfaces AxiosError 401s that
// flow through the response interceptor as "Uncaught runtime errors" — even
// though they are properly caught by AuthContext's try/catch — because the
// multi-microtask refresh-retry chain creates a brief window where the
// rejection appears detached from its synchronous await handler. React 19
// StrictMode amplifies this by running effects twice in dev.
//
// The overlay registers its listener via
// `window.addEventListener("unhandledrejection", fn)` (no capture flag).
// We register here with `{capture: true}` so our handler fires FIRST in the
// target phase regardless of listener-registration order, then call
// `stopImmediatePropagation()` to prevent the overlay's bubble-phase handler
// from seeing the event. Scoped tightly to 401s on the two auth probe paths
// where 401 is the expected baseline (no session). Production builds don't
// ship the overlay, so this has zero impact outside of CRA dev.
if (typeof window !== "undefined") {
  window.addEventListener(
    "unhandledrejection",
    (event) => {
      const reason = event.reason;
      const status = reason?.response?.status;
      const url = reason?.config?.url || "";
      if (status === 401 && (url.includes("/auth/me") || url.includes("/auth/refresh"))) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    { capture: true },
  );
}

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x && x.msg) || JSON.stringify(x)).join(" ");
  if (typeof d === "object") {
    if (d.compliance_failures) return "Compliance block: " + d.compliance_failures.join("; ");
    if (d.gps_violation) return `Outside geofence: ${d.distance_ft} ft from site (max ${d.radius_ft} ft)`;
    if (d.msg) return d.msg;
    return JSON.stringify(d);
  }
  return String(d);
}

export default api;
